import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Ticket,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import "./RetrieveRegistration.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const nowMs = Date.now();
const iso = (ms) => new Date(ms).toISOString();

// Static registration data for demo
const STATIC_REGISTRATIONS = {
  "VMT-AI-01": {
    ref_code: "VMT-AI-01",
    name: "Alex Chen",
    email_masked: "a***@example.com",
    payment_status: "free",
    event: {
      id: 1,
      title: "AI After Hours",
      start_time: iso(nowMs + 2 * 24 * 60 * 60 * 1000),
      end_time: iso(nowMs + 2 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
      timezone: "UTC",
    },
  },
  "VMT-CL-07": {
    ref_code: "VMT-CL-07",
    name: "Jordan Martinez",
    email_masked: "j***@company.com",
    payment_status: "paid",
    event: {
      id: 2,
      title: "Climate Capital Salon",
      start_time: iso(nowMs + 5 * 24 * 60 * 60 * 1000),
      end_time: iso(nowMs + 5 * 24 * 60 * 60 * 1000 + 3 * 60 * 60 * 1000),
      timezone: "America/New_York",
    },
  },
  "VMT-WEB3-99": {
    ref_code: "VMT-WEB3-99",
    name: "Sam Patel",
    email_masked: "s***@venture.io",
    payment_status: "voucher",
    event: {
      id: 3,
      title: "Web3 Builders Dinner",
      start_time: iso(nowMs + 7 * 24 * 60 * 60 * 1000),
      end_time: iso(nowMs + 7 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000),
      timezone: "Europe/London",
    },
  },
};

export default function RetrieveRegistration() {
  const [params, setParams] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState("");
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const signMeIn = async () => {
    if (!reg) return;
    const e = email.trim().toLowerCase();
    if (!e) {
      toast.error("Confirm the email this reference was sent to.");
      return;
    }
    setSigningIn(true);

    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      if (refresh) await refresh();
      toast.success("Signed in. Welcome back.");
      nav(`/events/${reg.event.id}`);
    } catch {
      toast.error("Email didn't match this reference.");
    } finally {
      setSigningIn(false);
    }
  };

  const lookup = async (code) => {
    setLoading(true);
    setErr("");
    setReg(null);

    await new Promise((resolve) => setTimeout(resolve, 600));

    try {
      const upperCode = code.toUpperCase();
      const found = STATIC_REGISTRATIONS[upperCode];

      if (found) {
        setReg(found);
      } else {
        setErr("We couldn't find that reference. Double-check the email.");
      }
    } catch {
      setErr("We couldn't find that reference. Double-check the email.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = params.get("ref");
    if (q && !reg && !loading) lookup(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const code = ref.trim().toUpperCase();
    if (!code) return;
    setParams({ ref: code });
    lookup(code);
  };

  return (
    <div data-testid="retrieve-page" className="retrievePage">
      <section
        className="retrieveHero"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x retrieveHeroInner" data-testid="retrieve-hero">
          <p className="overline retrieveOverline">Find my registration</p>

          <h1 className="font-display retrieveTitle">
            Lost the link?
            <br />
            <span className="retrieveTitleAccent">Use your reference.</span>
          </h1>

          <p className="retrieveIntro">
            Every confirmation email contains a reference code like{" "}
            <code className="retrieveCode">VMT-XXXX-XX</code>. Paste it here to
            open your event without signing in.
          </p>

          <form onSubmit={submit} className="glass retrieveForm" data-testid="retrieve-form">
            <input
              required
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="VMT-AI-01"
              className="retrieveInput"
              data-testid="retrieve-input"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-gold retrieveSubmit"
              data-testid="retrieve-submit"
            >
              {loading ? (
                "Looking up…"
              ) : (
                <>
                  Find my event <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {err && (
            <div className="retrieveError" data-testid="retrieve-error">
              <AlertCircle size={14} className="retrieveErrorIcon" /> {err}
            </div>
          )}

          {reg && reg.event && (
            <div className="glass retrieveResult" data-testid="retrieve-result">
              <div className="retrieveFoundRow">
                <CheckCircle2 size={16} />
                <span className="overline retrieveFoundOverline">
                  Found it — you're registered
                </span>
              </div>

              <div>
                <p className="overline retrieveEventWhen">
                  {new Date(reg.event.start_time).toLocaleString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    timeZone: reg.event.timezone,
                  })}
                </p>
                <h2 className="font-display retrieveEventTitle">{reg.event.title}</h2>
              </div>

              <div className="retrieveMetaGrid">
                <div>
                  <p className="overline retrieveMetaLabel">Registered to</p>
                  <p className="retrieveMetaValue">
                    {reg.name || reg.email_masked || "—"}
                  </p>
                </div>
                <div>
                  <p className="overline retrieveMetaLabel">Reference</p>
                  <p className="retrieveRefValue">{reg.ref_code}</p>
                </div>
                <div>
                  <p className="overline retrieveMetaLabel">Payment</p>
                  <PaymentBadge status={reg.payment_status} />
                </div>
              </div>

              <div className="retrieveActionRow">
                <Link
                  to={`/events/${reg.event.id}?ref=${encodeURIComponent(reg.ref_code)}`}
                  className="btn-gold"
                  data-testid="retrieve-open-event"
                >
                  Open event <ArrowRight size={16} />
                </Link>

                <a
                  href={`${BACKEND}/api/events/${reg.event.id}.ics`}
                  download={`vmeetio-${reg.event.id}.ics`}
                  className="btn-ghost retrieveGhostBtn"
                  data-testid="retrieve-ics"
                >
                  <Calendar size={14} /> Add to calendar
                </a>
              </div>

              <div className="retrieveSigninBlock" data-testid="retrieve-signin-block">
                <p className="overline retrieveSigninOverline">
                  <KeyRound size={11} className="retrieveInlineIcon" /> One-click sign in
                </p>

                <p className="retrieveSigninBody">
                  Confirm the email this reference was sent to and we'll sign
                  you in — no password needed. You'll land straight in the event
                  lobby with your full member view.
                </p>

                <div className="retrieveSigninRow">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={reg.email_masked || "you@example.com"}
                    className="retrieveSigninInput"
                    data-testid="retrieve-signin-email"
                    autoComplete="email"
                  />

                  <button
                    type="button"
                    onClick={signMeIn}
                    disabled={signingIn || !email.trim()}
                    data-testid="retrieve-signin-cta"
                    className="btn-gold retrieveSigninBtn"
                  >
                    {signingIn ? (
                      "Signing in…"
                    ) : (
                      <>
                        Sign me in <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>

                <p className="retrieveHint">
                  Hint: it usually starts with{" "}
                  <span className="retrieveHintAccent">{reg.email_masked}</span>
                </p>
              </div>

              <p className="retrieveAccountCta">
                Want to manage every event in one place?{" "}
                <Link to="/my-events" className="retrieveAccountLink">
                  Create an account →
                </Link>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PaymentBadge({ status }) {
  const map = {
    free: {
      label: "Free",
      cls: "payBadge payBadge--green",
    },
    voucher: {
      label: "Paid via voucher",
      cls: "payBadge payBadge--green",
    },
    paid: {
      label: "Paid",
      cls: "payBadge payBadge--green",
    },
    pending: {
      label: "Payment pending",
      cls: "payBadge payBadge--gold",
    },
  };
  const c = map[status] || map.free;

  return (
    <span className={c.cls} data-testid={`retrieve-payment-${status}`}>
      <Ticket size={10} /> {c.label}
    </span>
  );
}